'use client';

import { useState } from 'react';
import Link from 'next/link';
import { maxAffordablePrice } from '@/lib/affordability';
import { formatPriceFull } from '@/lib/utils';
import { Button } from '@/components/ui/Button';

const field =
  'w-full rounded-lg border border-hairline-strong bg-raised px-3 py-2 text-ink placeholder:text-ink-faint focus:border-emerald-500/60 focus:outline-none';

/** 28/36-rule affordability estimator; links straight into a capped search. */
export default function AffordabilityEstimator() {
  const [income, setIncome] = useState(200_000);
  const [debts, setDebts] = useState(500);
  const [down, setDown] = useState(150_000);
  const [rate, setRate] = useState(6.5);

  const result = maxAffordablePrice(income, debts, down, rate);

  return (
    <section className="glass mt-14 rounded-card p-6 sm:p-8">
      <h2 className="font-display text-2xl tracking-tight text-ink">What can I afford?</h2>
      <p className="mt-2 text-sm text-ink-muted">
        The 28/36 rule lenders start from: housing under 28% of gross income, total debts under
        36%. An estimate, not a pre-approval.
      </p>

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">
            Household income / yr
          </span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={5_000}
            value={income}
            onChange={(e) => setIncome(Number(e.target.value))}
            className={field}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">
            Monthly debts (cars, loans)
          </span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={100}
            value={debts}
            onChange={(e) => setDebts(Number(e.target.value))}
            className={field}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Down payment</span>
          <input
            type="number"
            inputMode="numeric"
            min={0}
            step={10_000}
            value={down}
            onChange={(e) => setDown(Number(e.target.value))}
            className={field}
          />
        </label>
        <label className="block">
          <span className="mb-1.5 block text-sm font-medium text-ink-soft">Rate %</span>
          <input
            type="number"
            inputMode="decimal"
            min={1}
            max={12}
            step={0.125}
            value={rate}
            onChange={(e) => setRate(Number(e.target.value))}
            className={field}
          />
        </label>
      </div>

      {result ? (
        <div className="mt-6 flex flex-wrap items-center justify-between gap-4 rounded-xl border border-emerald-500/25 bg-emerald-500/10 p-5">
          <div>
            <p className="text-sm text-ink-muted">Estimated max price</p>
            <p className="font-display text-3xl text-ink">{formatPriceFull(result.price)}</p>
            <p className="mt-1 text-sm text-ink-muted">
              ≈ {formatPriceFull(result.monthlyBudget)}/mo housing budget
            </p>
          </div>
          <Button asChild>
            <Link href={`/homes?priceMax=${result.price}`}>
              See homes under {formatPriceFull(result.price)}
            </Link>
          </Button>
        </div>
      ) : (
        <p className="mt-6 rounded-xl border border-hairline bg-glass p-5 text-sm text-ink-muted">
          Those numbers don&apos;t leave room for a housing payment yet — try adjusting income,
          debts, or down payment.
        </p>
      )}
    </section>
  );
}
